import {
  getContractTypeUsage,
  searchContractTypeClauses,
  searchContractTypeContracts,
  searchContractTypePlaybooks,
  type ContractTypeClauseSearchResult,
  type ContractTypeContractSearchResult,
  type ContractTypePlaybookSearchResult,
  type ContractTypeUsage,
  type SearchQuery,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

type Page = Pick<SearchQuery, 'text' | 'offset' | 'limit'>;

/** What references one contract type: the counts, and a page of each kind. */
export const ContractTypesService = {
  async usage(uid: string): Promise<ContractTypeUsage> {
    try {
      const response = await getContractTypeUsage({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to load contract type usage');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load contract type usage.'), {
        cause: error,
      });
    }
  },

  async searchContracts(uid: string, page: Page): Promise<ContractTypeContractSearchResult> {
    try {
      const response = await searchContractTypeContracts({ path: { uid }, body: page });
      if (!response.data) {
        throw new Error('Failed to search contracts');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search contracts.'), { cause: error });
    }
  },

  async searchPlaybooks(uid: string, page: Page): Promise<ContractTypePlaybookSearchResult> {
    try {
      const response = await searchContractTypePlaybooks({ path: { uid }, body: page });
      if (!response.data) {
        throw new Error('Failed to search playbooks');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search playbooks.'), { cause: error });
    }
  },

  async searchClauses(uid: string, page: Page): Promise<ContractTypeClauseSearchResult> {
    try {
      const response = await searchContractTypeClauses({ path: { uid }, body: page });
      if (!response.data) {
        throw new Error('Failed to search clauses');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search clauses.'), { cause: error });
    }
  },
};
